import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { toNumber } from 'lodash'
import { motion } from 'framer-motion'
import Skeleton from '@/components/skeleton'
import { isGroup } from '@/utils/listings'
import { colors, breakpoints } from '@/styles'
import { FavouriteHeart } from '@/components/icons'
import { CssUnit } from '@/types'
import config from '@/config'
import { VerifiedPill, PillWrapper } from './pills'
import CornerRibbon from './corner-ribbon'
import PriceDisplay from './pricing-display'
import { Row } from '../styles'

const { IMAGES_URL } = config

const CardWrapper = styled.div<{ blank?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 330px;
  width: ${props => (props.blank ? '330px' : '270px')};
  min-width: 270px;
  border: 1px solid ${colors.grey88};
  border-radius: 12px;
`

const ImageWrapper = styled.div`
  display: flex;
  flex: 3;
  position: relative;
  min-height: 203px;
  width: 100%;
  background: ${colors.grey96};
  border-bottom: 1px solid ${colors.grey88};
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  align-items: center;
  justfity-content: center;

  img {
    height: 203px;
  }
`

const Bottom = styled.div<{ gap: boolean | undefined }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: ${props => (props.gap ? '16px' : '0')};
  padding: 16px;
  flex: 2;
`

const Address = styled.div`
  font-size: 14px;
  color: ${colors.grey32};
  font-weight: 500;
`

const Blank = styled.div<{ width?: CssUnit }>`
  display: flex;
  border-radius: 4px;
  background: ${colors.grey96};
  height: 40px;
  width: ${props => (props.width ? props.width : '100%')};
`

const Button = styled.div<{ submit?: boolean }>`
  display: flex;
  border-radius: 4px;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${props => (props.submit ? colors.white : colors.black)};
  background: ${props => (props.submit ? colors.blue16 : colors.white)};
  ${props => !props.submit && `border: 1px solid ${colors.grey88}`};
  height: 40px;
  width: 100%;
  text-align: center;
`

const FavouriteButtonStyled = styled(FavouriteHeart)`
  z-index: 10;

  svg {
    width: 22px;
    height: 20px;
  }

  :hover {
    svg {
      transition: transform 0.2s;
      transform: scale(1.05);
    }
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
`

const priceStyles = css`
  color: ${colors.white};
`

const frequencyStyles = css`
  color: ${colors.white};
  font-size: 14px;
  font-weight: 500;
`

const FeaturedPrice = styled(motion.div)`
  padding: 4px 8px;
  height: 36px;
  min-width: 148px;
  background: ${colors.black};
  position: absolute;
  bottom: 0px;
  left: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
`
const StyledRow = styled(Row)`
  gap: 8px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: row;
  }
`

type Props = {
  blank?: boolean
  featured?: boolean
  listing?: any
  loading?: boolean
}

const getPrice = (listing: any) =>
  isGroup(listing) ? { min: listing.gr_min_price, max: listing.gr_max_price } : listing.price

const ListingCard = ({ blank, featured, listing = {}, loading }: Props) => {
  const { t } = useTranslation()
  const bedroomCount = toNumber(listing.bedrooms)
  const bathroomCount = toNumber(listing.bathrooms)
  const listingSize = toNumber(listing.size)
  const bedroomText =
    bedroomCount > 0
      ? t('b.listing.view.count_beds.text', {
          defaultValue: '{{ bedroomCount }} Bed',
          bedroomCount
        })
      : t('common.studio', 'Studio')
  const bathroomText = t('b.listing.view.count_baths.text', {
    defaultValue: '{{ bathroomCount }} Bath',
    bathroomCount
  })
  const sizeText = t('b.listing.view.size.text', {
    defaultValue: '{{ listingSize }} ft²',
    listingSize
  })

  const cover = `${IMAGES_URL}/${listing?.cover_photo_aws_s3_key}`
  const Pills = () => <PillWrapper>{featured && <VerifiedPill />}</PillWrapper>

  return (
    <CardWrapper blank={blank}>
      <ImageWrapper>
        {featured && <Pills />}
        {featured &&
          (cover ? (
            <img
              src={cover}
              alt="Listing"
              style={{ objectFit: 'cover', height: '203px', width: '100%' }}
            />
          ) : (
            <Skeleton height={'203px'} width={'100%'} />
          ))}
        {featured && (
          <CornerRibbon>{t('b.listing.view.sponsored_banner.text', 'Sponsored')}</CornerRibbon>
        )}
        {featured && !loading && (
          <FeaturedPrice
            initial={{ opacity: 0 }}
            animate={{
              opacity: loading ? 0 : 1
            }}
          >
            <PriceDisplay
              price={getPrice(listing)}
              frequency={listing.price_frequency}
              priceStyles={priceStyles}
              frequencyStyles={frequencyStyles}
              center
            />
          </FeaturedPrice>
        )}
      </ImageWrapper>
      <Bottom gap={!featured || loading}>
        {featured ? (
          !loading ? (
            <Column>
              <Row style={{ marginBottom: '-8px' }}>
                <Address style={{ fontWeight: 'bold' }}>{listing?.full_street_name}</Address>
                <FavouriteButtonStyled title="Add to Favourites" />
              </Row>
              <Row style={{ width: '238px', height: '40px' }}>
                <Address>
                  {listing.unit_type_scope_txt_id === 'single'
                    ? t('common.room', 'Room')
                    : `${bedroomText} · ${bathroomText} · ${sizeText}`}
                </Address>
              </Row>
            </Column>
          ) : (
            <Skeleton height={35} width={238} />
          )
        ) : (
          <Blank width={'238px'} />
        )}
        <Row style={{ gap: '8px' }}>
          {featured ? (
            <StyledRow>
              <Button>Message</Button>
              <Button submit={true}>Apply Now</Button>
            </StyledRow>
          ) : (
            <StyledRow>
              <Blank width={'115px'} />
              <Blank width={'115px'} />
            </StyledRow>
          )}
        </Row>
      </Bottom>
    </CardWrapper>
  )
}

export default ListingCard
